/**
 * The status model (design spec §4) and what each status permits.
 *
 * Two refinements from the architecture step (design spec §12, "Deltas") are
 * encoded here rather than left to each screen:
 *
 *  - `ANULADO` splits by cause. A **superseded** document keeps its direct
 *    download until its normal 14-day expiry (BOE Third.2 forbids an early
 *    expiry, Fifth.2 requires the original to be retained); a **cancelled** one
 *    loses it immediately. The single "download ✗" of §4 was too coarse.
 *  - `CADUCADO` disables only the *direct* path. Authenticated download survives
 *    for the retention year (BOE Second.4).
 */

import type {
  CurrentPrincipal,
  Deca,
  DecaEstado,
  DecaSummary,
  PassengerDeca,
  VoidReason,
} from '@deca/api'

import { sameIdentifier } from './identifiers.ts'

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral'

export interface EstadoStyle {
  tone: Tone
  /** Rendered at reduced emphasis — the "-muted" variants of design spec §4. */
  muted: boolean
  icon: EstadoIcon
}

export type EstadoIcon = 'draft' | 'inTransit' | 'edited' | 'check' | 'clock' | 'ban'

const STYLES: Record<DecaEstado, EstadoStyle> = {
  BORRADOR: { tone: 'neutral', muted: false, icon: 'draft' },
  'EN CURSO': { tone: 'info', muted: false, icon: 'inTransit' },
  MODIFICADO: { tone: 'warning', muted: false, icon: 'edited' },
  FINALIZADO: { tone: 'success', muted: false, icon: 'check' },
  CADUCADO: { tone: 'neutral', muted: true, icon: 'clock' },
  ANULADO: { tone: 'danger', muted: true, icon: 'ban' },
}

/**
 * The direct-download window, in days.
 *
 * ⚠️ The comment this replaces said both windows were policy the API does not carry, so the portal
 * had to hold its own copies — 14 days and 24 months. That was true of the contract and false of
 * the system. `directDownloadDays` is **per organisation** (7–30, set by a platform super-admin on
 * screen 11), and since 2026-08-12 it is what the backend actually issues documents under, so a
 * constant here is a confident wrong number for every client not on the default. It now travels on
 * the principal, already resolved.
 *
 * Retention has no constant at all any more. The enforced period is the organisation's
 * `retentionMonths` and it reaches the reader as `retainUntil` — a date, which needs no window to
 * render. The PDF's «Conservación mínima 12 meses» is a different statement, the BOE Second.4
 * floor, true of every document however long this organisation keeps it.
 */
export const DEFAULT_DIRECT_DOWNLOAD_DAYS = 14

/**
 * The window this principal's organisation actually gets.
 *
 * Only for the places with no date to show yet — the create screen's rail, a completion
 * confirmation. Wherever the document exists, render `directDownloadExpiresAt` instead: it is the
 * server's own answer rather than the portal's arithmetic on top of a policy.
 */
export function downloadWindowDays(principal: CurrentPrincipal | null | undefined): number {
  return principal?.organisation.directDownloadDays ?? DEFAULT_DIRECT_DOWNLOAD_DAYS
}

export function estadoStyle(estado: DecaEstado): EstadoStyle {
  return STYLES[estado]
}

/** i18n key for a status label. Kept in one place so no screen inlines it. */
export function estadoKey(estado: DecaEstado): string {
  return `estado.${estado.replace(' ', '_')}`
}

/**
 * i18n key for the **plural** label — "Modificadas", not "Modificado".
 *
 * The two forms are not interchangeable and both appear on screen at once. A
 * chip names the state of one document, so it agrees with *el estado*: the
 * masculine singular the API's own vocabulary uses. A filter pill or a summary
 * row counts documents, so it agrees with *el DeCA* — masculine, as ASGTRANS
 * settled on 2026-10-07 (*el documento*: "Anular el DeCA"); it was feminine
 * before. The plural is still a second key: the singular agrees with *el estado*.
 */
export function estadoPluralKey(estado: DecaEstado): string {
  return `estadoPlural.${estado.replace(' ', '_')}`
}

type AnyDeca = Deca | DecaSummary

/**
 * The lifecycle question, for either document family.
 *
 * Structural rather than `AnyDeca`, and widened here in Task 10 when the route sheet's
 * own detail screen landed: `PassengerDeca.estado` is the same `DecaEstado`, and the
 * contract says why — "The same six states, deliberately. The Resolution's lifecycle
 * clauses are written over 'the DeCA files' generically and apply to a route sheet
 * unchanged." Anything that reads a *goods* field (`canGroup`'s `isGrouped`,
 * `samePartiesAs`'s two NIFs) stays on `AnyDeca` and must.
 */
interface HasEstado {
  estado: DecaEstado
}

function estadoOf(deca: HasEstado): DecaEstado {
  return deca.estado
}

/**
 * Deliberately **not** a structural `{ voidReason?: … }`.
 *
 * A `HasDownloadWindow` interface used to sit here and be what `directDownloadLive`
 * took, which let both summary types satisfy it — they have a `voidReason` and no
 * `directDownloadExpiresAt`, and every member of that interface was optional. This
 * takes the same union its one caller does, so the two cannot drift apart again.
 */
function voidReasonOf(deca: Deca | PassengerDeca): VoidReason | null | undefined {
  return deca.voidReason
}

/**
 * Generated at all — i.e. has a PDF, a QR, a CSV and a document number.
 *
 * The **widest** parameter in this file: the bare `estado`, so that any of the
 * four document shapes can be asked it. It has to be. `DecaListScreen` asks it of
 * a `DecaSummary` and of a `PassengerDecaSummary` in the same table, and the two
 * create forms ask it of a freshly returned `Deca` and `PassengerDeca`. The
 * contract gives `PassengerDeca.estado` the same `DecaEstado` and says why: "The
 * same six states, deliberately. The Resolution's lifecycle clauses are written
 * over 'the DeCA files' generically and apply to a route sheet unchanged."
 *
 * ↻ This docblock claimed to be the only structural parameter here. It is not,
 * and was not when it was written — `HasEstado`, `HasParties` and
 * `HasDownloadWindow` all predate it. What is still true, and is the point, is
 * that each of those names exactly what its function reads, and that a structural
 * parameter is only as safe as the members it demands: `canGroup` stays on
 * `AnyDeca` because `isGrouped` means the goods grouping the list screen
 * assembles from issued documents, which has no passenger counterpart — a route
 * sheet is grouped at creation — and `directDownloadLive` takes the two full
 * documents outright, because the field it reads exists on nothing else.
 */
export function isGenerated(deca: { estado: DecaEstado }): boolean {
  return deca.estado !== 'BORRADOR'
}

/**
 * In service: `EN CURSO` or `MODIFICADO`. What a document offers only in service
 * hangs on it: «Regenerar como documento nuevo» (BOE Fifth.2), cancelling,
 * completing and grouping. Amendable in place (Fifth.1) is `canAmend`, which a
 * finished document passes too (#180).
 */
export function canModify(deca: HasEstado): boolean {
  const estado = estadoOf(deca)
  return estado === 'EN CURSO' || estado === 'MODIFICADO'
}

/**
 * Amendable in place (#180): in service, or finished, which the date rule may return to service. Not
 * `canModify`, which also gates «Regenerar», cancelling, completing and grouping: those stay in service only.
 */
export function canAmend(deca: HasEstado): boolean {
  return canModify(deca) || estadoOf(deca) === 'FINALIZADO'
}

export function canVoid(deca: HasEstado): boolean {
  return canModify(deca)
}

export function canComplete(deca: HasEstado): boolean {
  return canModify(deca)
}

/** The two NIFs, in the only shape this file needs them — `Deca` and `DecaSummary` both fit. */
interface HasParties {
  cargador_nif?: string | null
  transportista_nif?: string | null
}

/**
 * Same contractual shipper, same actual carrier — the portal's copy of the
 * backend's `partiesDiffer` (`backend/src/deca/grouping.ts`), inverted.
 *
 * **Normalised, not `===`.** The backend compares both NIFs through
 * `normaliseIdentifier` on purpose: `A-41118829` and `A41118829` pass the same
 * checksum, so both spellings are storable (the importer and the external
 * TMS/ERP `PATCH` normalise nothing on write), and they are one legal person.
 * A raw comparison here refused a grouping the server would have accepted, and
 * left the user nowhere to go — `cargador_nif` is a party field, so it cannot
 * be amended in place and "fixing" it means superseding the document.
 *
 * One function for both call sites (`canGroupTogether` below and `GroupScreen`'s
 * pre-submit check), because the portal had written this rule twice and the two
 * copies are exactly what drifted from the backend together.
 */
export function samePartiesAs(deca: HasParties, reference: HasParties): boolean {
  return (
    sameIdentifier(deca.cargador_nif, reference.cargador_nif) &&
    sameIdentifier(deca.transportista_nif, reference.transportista_nif)
  )
}

/**
 * May these documents become one grouped DeCA? — BOE Sixth.
 *
 * Two conditions: every document must still be groupable on its own (in
 * service, not already grouped), and the **contractual shipper and the actual
 * carrier must be identical across all of them**. Fewer than two is not a
 * grouping.
 *
 * This lives here, not in the list screen, because it is the rule the server
 * enforces with a 422 — the portal only stops the user assembling a request
 * that cannot succeed, and both places have to mean the same thing by it.
 *
 * Deliberately *weaker* than the server's check in one respect, and it stays
 * that way: the backend also refuses a differing tractor, trailer or driver
 * (`VEHICLE_OR_DRIVER_DIFFERS`), which the list rows do not carry. The portal
 * must never refuse what the server would accept; it may accept what the server
 * then refuses, which is what the 422's `conflicts` list is for.
 */
export function canGroupTogether(decas: readonly DecaSummary[]): boolean {
  if (decas.length < 2) return false
  if (!decas.every(canGroup)) return false
  const first = decas[0]
  if (!first) return false
  return decas.every((deca) => samePartiesAs(deca, first))
}

/**
 * `POST /decas/group`'s `documentIds` cap (`maxItems: 50`, `deca-openapi.yaml`).
 *
 * `GroupScreen` reads it before fanning out one `GET /decas/{id}` per selected id — nothing
 * upstream of that screen enforces the cap (`DecaListScreen`'s selection is component state
 * that survives pagination, and the URL schema accepts any array length), so a large enough
 * selection would otherwise fire that many concurrent reads at a `t4g.small` that is also
 * running Postgres.
 */
export const MAX_GROUP_SELECTION = 50

export function canDeleteDraft(deca: HasEstado): boolean {
  return estadoOf(deca) === 'BORRADOR'
}

export function canGroup(deca: AnyDeca): boolean {
  return canModify(deca) && !deca.isGrouped
}

/**
 * Whether `/d/{token}` still serves the PDF.
 *
 * Two conditions, because the server has two. The estado closes a draft, a
 * lapsed document and a *cancelled* one — `ANULADO` depends on why, which is
 * the refinement the architecture step introduced. The expiry closes everything
 * else, and it is not the same question: `deca.expire-direct-download` relabels
 * an expired document `CADUCADO` only `where estado = 'FINALIZADO'`, on purpose,
 * because BOE Fifth.2 requires a superseded original's link to run to its own
 * expiry instead of being closed by the supersede.
 *
 * So a document completed — which is what sets the expiry — and then superseded
 * inside its fourteen days lands on `ANULADO`/`SUPERSEDED` holding a real expiry
 * that no job will ever act on. `download.ts` stops serving it on the day and
 * the estado never moves again, so reading the estado alone told a gestor the
 * driver's QR was live for the rest of time.
 *
 * A **null** expiry is the in-service case and stays open: BOE Third.2 forbids
 * the link closing before the service ends, and folding that null in as "past"
 * would shut every document currently on the road. `download.ts` spells the
 * same condition the same way round.
 *
 * ↻ **Widened in Task 10 so a route sheet can be asked it too** — its `/d/{token}`
 * is the same public path under the same clauses, and a second copy of this rule
 * is exactly what `samePartiesAs` above records having drifted last time. The
 * widening is to `Deca | PassengerDeca`, and not to a structural parameter.
 *
 * 🚨 **The original `Deca` was a real guard and this keeps it.** A revision of this
 * docblock claimed `DecaSummary` carries `directDownloadExpiresAt` and that the
 * guard had therefore never been real. It does not: the contract declares that
 * field on `Deca` and `PassengerDeca` only, and both summary schemas carry
 * `directDownloadUrl` and `generatedAt` and nothing more. Ask this of a summary
 * and the expiry reads `undefined`, the `if (!expiresAt) return true` below takes
 * it for the in-service case, and the answer is "the driver's QR is live" about a
 * public link that stopped serving days ago — a false statement to a gestor about
 * a legal document, from a function that cannot tell it is missing half its input.
 *
 * A structural parameter cannot express that, because the field is optional on
 * `Deca` too (the contract does not require it), so an interface demanding it
 * would exclude the very types that are allowed. Hence the union: the two shapes
 * that carry the window, named outright. No caller loses anything — both call
 * sites are detail screens holding a full document.
 */
export function directDownloadLive(
  deca: Deca | PassengerDeca,
  now: number = Date.now(),
): boolean {
  const openByEstado = (() => {
    switch (estadoOf(deca)) {
      case 'BORRADOR':
      case 'CADUCADO':
        return false
      case 'ANULADO':
        return voidReasonOf(deca) === 'SUPERSEDED'
      default:
        return true
    }
  })()
  if (!openByEstado) return false

  const expiresAt = deca.directDownloadExpiresAt
  if (!expiresAt) return true
  return Date.parse(expiresAt) > now
}

/** The authenticated route survives everything except "never generated". */
export function authenticatedDownloadAvailable(deca: HasEstado): boolean {
  return isGenerated(deca)
}

/** Fired when the direct-download window closes inside 48 hours. */
export function expiresSoon(expiresAt: string | null | undefined, now = Date.now()): boolean {
  if (!expiresAt) return false
  const ms = Date.parse(expiresAt) - now
  return ms > 0 && ms < 48 * 60 * 60 * 1000
}
